import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import { memo, useCallback } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import AireList from 'system/components/shared/aire-list';
import Foreign from 'shell/components/shared/consult-person/steps/registry-office/generality/foreign';
import Italian from 'shell/components/shared/consult-person/steps/registry-office/generality/italian';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import RegistrationReason from 'shell/components/shared/registration-reason';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';

interface FormGeneralityProps {
  isEditMode?: boolean;
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
  disabledBirthDate?: boolean;
  labelMunicipalityItalian?: string;
  labelProvinceItalian?: string;
  ForPracticeTemporary?: boolean;
  requiredFiscalCode?: boolean;
  disabledValidityFiscalCode?: boolean;
}

const FormGeneralityComponent = ({
  isEditMode,
  disabled,
  generality,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangeFieldsGenerality,
  ForPracticeTemporary,
  disabledBirthDate = false,
  labelMunicipalityItalian,
  labelProvinceItalian,
  requiredFiscalCode = false,
}: FormGeneralityProps): React.ReactElement => {
  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreign', {
        ...(generality || {}).foreign,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('gender', event.target.value);
  });

  const handleAireListChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('aire', event.target.value);
  });

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeFieldsGenerality({
        birthDate: eventDate,
        birthChoiceDate: choice,
      });
    }
  );
  const formContext = useFormikContext<NewPersonVM>();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label={requiredFiscalCode ? 'Codice fiscale *' : 'Codice fiscale'}
          aria-label="Codice fiscale"
          value={generality?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => {
            onChangeGenerality('fiscalCode', event.target.value);
          }}
          helperText={errorsGenerality?.fiscalCode}
          error={touchedGenerality?.fiscalCode && Boolean(errorsGenerality?.fiscalCode)}
          fullWidth
          disabled={isEditMode ? false : disabled}
          inputProps={{ maxLength: '16' }}
        />
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={generality?.withoutSurname ? 'Cognome' : 'Cognome *'}
            aria-label="Cognome"
            id="surname"
            name="civilUnion.generality.surname"
            fullWidth
            onBlur={formContext?.handleBlur}
            disabled={isEditMode && !generality?.withoutSurname ? false : disabled}
            helperText={errorsGenerality?.surname}
            error={touchedGenerality?.surname && Boolean(errorsGenerality?.surname)}
            value={generality?.surname || ''}
            inputProps={{
              maxLength: '80',
            }}
            onChange={(event) => {
              onChangeGenerality('surname', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname-replica"
                name="civilUnion.generality.withoutSurname"
                checked={generality?.withoutSurname || false}
                onChange={() => {
                  onChangeFieldsGenerality({
                    withoutSurname: !generality?.withoutSurname,
                    surname: !generality?.withoutSurname ? '' : generality?.surname,
                  });
                }}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2} xs={12} md={10} alignItems="center">
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={generality?.withoutName ? 'Nome' : 'Nome *'}
            aria-label="Nome"
            id="name"
            name="civilUnion.generality.name"
            onBlur={formContext?.handleBlur}
            fullWidth
            disabled={isEditMode && !generality?.withoutName ? false : disabled}
            inputProps={{
              maxLength: '80',
            }}
            helperText={errorsGenerality?.name}
            error={touchedGenerality?.name && Boolean(errorsGenerality?.name)}
            value={generality?.name || ''}
            onChange={(event) => {
              onChangeGenerality('name', event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName-replica"
                name="civilUnion.generality.withoutName"
                checked={generality?.withoutName || false}
                onChange={() => {
                  onChangeFieldsGenerality({
                    withoutName: !generality?.withoutName,
                    name: !generality?.withoutName ? '' : generality?.name,
                  });
                }}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md={4}>
          <ListGender
            required
            value={generality?.gender || ''}
            onChange={handleListGenderChange}
            disabled={isEditMode ? false : disabled}
            error={touchedGenerality?.gender && Boolean(errorsGenerality?.gender)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AireList
            value={generality?.aire || ''}
            onChange={handleAireListChange}
            disabled={isEditMode ? false : disabled}
            error={Boolean(errorsGenerality?.aire)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {generality?.isForeign ? (
            <PmiTextField
              label="Anno Espatrio"
              id="expatriation-year"
              fullWidth
              onBlur={formContext?.handleBlur}
              disabled={isEditMode ? false : disabled}
              helperText={errorsGenerality?.expatriationYear}
              error={
                touchedGenerality?.expatriationYear && Boolean(errorsGenerality?.expatriationYear)
              }
              aria-label="Anno Espatrio"
              value={generality?.expatriationYear || ''}
              onChange={(event) => {
                onChangeGenerality(
                  'expatriationYear',
                  event.target.validity ? event.target.value : generality?.expatriationYear
                );
              }}
              inputProps={{ maxLength: '4' }}
            />
          ) : undefined}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          selected={generality?.birthDate || null}
          onChange={handleChooseDateChange}
          choice={generality?.birthChoiceDate}
          disabled={isEditMode ? false : disabled || disabledBirthDate}
          error={touchedGenerality?.birthDate && Boolean(errorsGenerality?.birthDate)}
          helperText={errorsGenerality?.birthDate}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            name="civilUnion.generality.isForeign"
            id="isForeign"
            value={generality?.isForeign || 'false'}
            onChange={() => {
              onChangeFieldsGenerality({
                isForeign: !generality?.isForeign,
                expatriationYear: '',
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
            }}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Nascita Italia"
              disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Nascita Estero"
              disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!generality?.isForeign ? (
        <Italian
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.italian}
          onChange={onFieldItalianChange}
          isMunicipalityRequired={false}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
          touched={touchedGenerality?.italian as unknown as FormikTouched<ItalianForm>}
          labelMunicipality={labelMunicipalityItalian}
          labelProvince={labelProvinceItalian}
          birthday={generality?.birthDate}
        />
      ) : (
        <Foreign
          disabledComponent={isEditMode ? false : disabled}
          values={generality?.foreign}
          isForeignStateRequired={false}
          isForeignMunicipalityRequired={false}
          onChange={onFieldForeignChange}
          errors={errorsGenerality?.foreign as FormikErrors<ForeignForm>}
          touched={touchedGenerality?.foreign as unknown as FormikTouched<ForeignForm>}
        />
      )}
      {ForPracticeTemporary ? (
        <Grid item xs={12}>
          <RegistrationReason
            label="Motivo registrazione"
            value={generality?.registrationReason || ''}
            required
            disabled={isEditMode ? false : disabled}
            onChange={(event) => onChangeGenerality('registrationReason', event.target.value)}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormGeneralityComponent.displayName = 'Form generality component';

const FormGenerality = memo(FormGeneralityComponent);

export default FormGenerality;
